import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/contexts/AuthContext';
import { getStorageUrl } from '@/utils/storageUrl';
import { ContactInboxButton } from './ContactInboxButton';
import {
  Bars3Icon,
  ShieldCheckIcon,
  UserCircleIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export interface AppHeaderProps {
  /** Contrôle du menu hamburger mobile */
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

/**
 * Header global de l'application.
 * Utilisé par le layout principal. Inclut le logo, le toggle thème, les infos utilisateur et la déconnexion.
 */
export function AppHeader({ sidebarOpen, onToggleSidebar }: AppHeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const getUserRoleIcon = () => {
    if (user?.email === 'admin@luxori.com') {
      return <ShieldCheckIcon className="h-6 w-6 text-red-500" />;
    }
    switch (user?.role) {
      case 'admin':
        return <ShieldCheckIcon className="h-6 w-6 text-red-500" />;
      case 'gerant':
        return <UserCircleIcon className="h-6 w-6 text-blue-500" />;
      case 'vendeur':
        return <UserIcon className="h-6 w-6 text-green-500" />;
      default:
        return <UserIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  return (
    <header className="layout-header shadow-lg flex-shrink-0">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo et titre avec bouton hamburger */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onToggleSidebar}
              className="layout-header-btn-hover p-2 rounded-token-md focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus lg:hidden transition-colors duration-200"
              aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              {sidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
            <button
              onClick={() => navigate(ROUTES.HOME)}
              className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus rounded"
            >
              <img
                src={getStorageUrl('logos/logo_Luxori.png')}
                alt="Luxori Logo"
                className="h-6 w-auto object-contain"
              />
              <h1 className="text-2xl font-bold">LUXORI</h1>
              <span className="layout-header-subtext text-sm hidden sm:block">
                GESTION COMMERCIALE
              </span>
            </button>
          </div>

          {/* Informations utilisateur et déconnexion */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getUserRoleIcon()}
                <div className="text-right hidden sm:block">
                  <div className="text-xs font-medium">
                    {user?.prenom} {user?.nom}
                  </div>
                  <div className="layout-header-subtext text-xs text-center">Sécurisé</div>
                </div>
              </div>
              <ContactInboxButton />
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <span className="hidden sm:inline">Déconnexion</span>
                <span className="sm:hidden">✕</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
